import { App } from "@capacitor/app";
import CssBaseline from "@material-ui/core/CssBaseline";
import { ThemeProvider } from "@material-ui/core/styles";
import { SnackbarProvider } from "notistack";
import * as React from "react";
import { BrowserRouter as Router, Redirect, Route, Switch } from "react-router-dom";
import { RecoilRoot } from "recoil";

import { AccountRegisterBasicPage } from "@/pages/account_register_basic/AccountRegisterBasicPage";
import { AgentDetailPage } from "@/pages/agent_detail/AgentDetailPage";
import { AgentSearchPage } from "@/pages/agent_search/AgentSearchPage";
import { BalancePage } from "@/pages/balance/BalancePage";
import { DiagnosisPage } from "@/pages/diagnosis/DiagnosisPage";
import { DiagnosisCompatibilityAgentsCertifiedPage } from "@/pages/diagnosis_compatibility_agents_certified/DiagnosisCompatibilityAgentsCertifiedPage";
import { DiagnosisCompatibilityAgentsIntroPage } from "@/pages/diagnosis_compatibility_agents_intro/DiagnosisCompatibilityAgentsIntroPage";
import { DiagnosisCompatibilityAgentsResultPage } from "@/pages/diagnosis_compatibility_agents_result/DiagnosisCompatibilityAgentsResultPage";
import { DiagnosisInvestmentStylePage } from "@/pages/diagnosis_investment_style/DiagnosisInvestmentStylePage";
import { DiagnosisInvestmentStyleCompletionPage } from "@/pages/diagnosis_investment_style_completion/DiagnosisInvestmentStyleCompletionPage";
import { DiagnosisInvestmentStyleIntroPage } from "@/pages/diagnosis_investment_style_intro/DiagnosisInvestmentStyleIntroPage";
import { DiagnosisInvestmentStyleResultPage } from "@/pages/diagnosis_investment_style_result/DiagnosisInvestmentStyleResultPage";
import { DiagnosisPrePage } from "@/pages/diagnosis_pre/DiagnosisPrePage";
import { LearningArticlePage } from "@/pages/learning_article/LearningArticlePage";
import { LearningStagePage } from "@/pages/learning_stage/LearningStagePage";
import { LearningTopPage } from "@/pages/learning_top/LearningTopPage";
import { LimitedPropertiesListPage } from "@/pages/limited_properties_list/LimitedPropertiesListPage";
import { LimitedPropertyDetailPage } from "@/pages/limited_property_detail/LimitedPropertyDetailPage";
import { LoginPage } from "@/pages/login/LoginPage";
import { ManagementAddNewConsiderationPropertyPage } from "@/pages/management_add_new_consideration_property/ManagementAddNewConsiderationPropertyPage";
import { ManagementAddNewOwnBuildingPropertyPage } from "@/pages/management_add_new_own_building_property/ManagementAddNewOwnBuildingPropertyPage";
import { ManagementAddNewOwnPropertyPage } from "@/pages/management_add_new_own_property/ManagementAddNewOwnPropertyPage";
import { ManagementConsiderationPage } from "@/pages/management_consideration/ManagementConsiderationPage";
import { ManagementConsiderationDetailPage } from "@/pages/management_consideration_detail/ManagementConsiderationDetailPage";
import { ManagementDetailPage } from "@/pages/management_detail/ManagementDetailPage";
import { ManagementDetailSettingsPage } from "@/pages/management_detail_settings/ManagementDetailSettingsPage";
import { ManagementDetailSimulationConfigPage } from "@/pages/management_detail_simulation_config/ManagementDetailSimulationConfigPage";
import { ManagementEditPropertyPage } from "@/pages/management_edit_property/ManagementEditPropertyPage";
import { ManagementEditRoomPage } from "@/pages/management_edit_room/ManagementEditRoomPage";
import { ManagementOwnPage } from "@/pages/management_own/ManagementOwnPage";
import { ManagementPropertyDocumentCategoryListPage } from "@/pages/management_property_document_category_list/ManagementPropertyDocumentCategoryListPage";
import { ManagementPropertyDocumentListPage } from "@/pages/management_property_document_list/ManagementPropertyDocumentListPage";
import { MypagePage } from "@/pages/mypage/MypagePage";
import { NotFoundPage } from "@/pages/not_found/NotFoundPage";
import { OfferAssessmentDetailPage } from "@/pages/offer_assessment_detail/OfferAssessmentDetailPage";
import { OfferInvestmentDetailPage } from "@/pages/offer_investment_detail/OfferInvestmentDetailPage";
import { OfferListPage } from "@/pages/offer_list/OfferListPage";
import { OnboardingPage } from "@/pages/onboarding/OnboardingPage";
import { PasswordResetPage } from "@/pages/password_reset/PasswordResetPage";
import { PasswordResetSendLinkPage } from "@/pages/password_reset_send_link/PasswordResetSendLinkPage";
import { PremiumWebinarDetailPage } from "@/pages/premium_webinar_detail/PremiumWebinarDetailPage";
import { PremiumWebinarListPage } from "@/pages/premium_webinar_list/PremiumWebinarListPage";
import { RegisterPage } from "@/pages/register/RegisterPage";
import { RegistrationCompletePage } from "@/pages/registration_complete/RegistrationCompletePage";
import { RegistrationIntroPage } from "@/pages/registration_intro/RegistrationIntroPage";
import { SettingsPage } from "@/pages/settings/SettingsPage";
import { UsageGuidePage } from "@/pages/usage_guide/UsageGuidePage";
import { isBackButtonBlocked } from "@/utilities/backButtonGuard";
import { PreviousLocationProvider } from "@/utilities/PreviousLocation";
import ScrollToTop from "@/utilities/ScrollToTop";

import { AppVersionChecker } from "./AppVersionChecker";
import DeepLinkRouter from "./DeepLinkRouter";
import NotificationRouter from "./NotificationRouter";
import { PropallyRecoilRoot } from "./RecoilRoot";
import { theme } from "./theme";
import { UpdateModal } from "./UpdateModal";
import { WithAuth } from "./WithAuth";

export const PropallyApp: React.FC = () => {
  // Handle back-button of Android.
  // See https://capacitorjs.com/docs/apis/app#addlistenerbackbutton
  const onBackButtonPushed = ({ canGoBack }) => {
    // 「保存するまで閉じられない」モーダル表示中は戻る操作でモーダルを回避させない。
    if (isBackButtonBlocked()) {
      return;
    }
    if (canGoBack) {
      history.back();
    }
  };
  React.useEffect(() => {
    App.addListener("backButton", onBackButtonPushed);
    return () => {
      App.removeAllListeners();
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      {/** MEMO: Reset CSS */}
      <CssBaseline />
      <SnackbarProvider
        maxSnack={3}
        preventDuplicate
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <RecoilRoot>
          <Router>
            <DeepLinkRouter />
            <NotificationRouter />
            <AppVersionChecker />
            <UpdateModal />
            <ScrollToTop />
            <Switch>
              {/* 利用予定がないためコメントアウト */}
              {/* <Route exact path="/component" component={ComponentTest} /> */}
              <Route exact path="/login" component={LoginPage} />
              <Route exact path="/registrations" component={RegisterPage} />
              <Route exact path="/registration_intro" component={RegistrationIntroPage} />
              <Route exact path="/pre_diagnosis" component={DiagnosisPrePage} />
              <Route exact path="/password_reset" component={PasswordResetPage} />
              <Route exact path="/password_reset/new" component={PasswordResetSendLinkPage} />
              <Route exact path={["/", "/onboarding"]} component={OnboardingPage} />

              <WithAuth>
                <PropallyRecoilRoot>
                  <PreviousLocationProvider>
                    <Switch>
                      <Route exact path="/first_account" component={AccountRegisterBasicPage} />
                      <Route exact path="/registration_complete" component={RegistrationCompletePage} />
                      <Route exact path="/diagnosis" component={DiagnosisPage} />
                      <Route exact path="/investment_style_diagnosis" component={DiagnosisInvestmentStylePage} />
                      <Route
                        exact
                        path="/investment_style_diagnosis/intro"
                        component={DiagnosisInvestmentStyleIntroPage}
                      />
                      <Route
                        exact
                        path="/investment_style_diagnosis/completion"
                        component={DiagnosisInvestmentStyleCompletionPage}
                      />
                      <Route
                        exact
                        path="/investment_style_diagnosis/result"
                        component={DiagnosisInvestmentStyleResultPage}
                      />
                      <Route
                        exact
                        path="/diagnosis_compatibility_agents/start"
                        render={() => <Redirect to="/diagnosis_compatibility_agents/intro" />}
                      />
                      <Route
                        exact
                        path="/diagnosis_compatibility_agents/intro"
                        component={DiagnosisCompatibilityAgentsIntroPage}
                      />
                      <Route
                        exact
                        path="/diagnosis_compatibility_agents/certified"
                        component={DiagnosisCompatibilityAgentsCertifiedPage}
                      />
                      <Route
                        exact
                        path="/diagnosis_compatibility_agents/result"
                        component={DiagnosisCompatibilityAgentsResultPage}
                      />
                      <Route path="/mypage" component={MypagePage} />
                      <Route exact path="/property_document_list" component={ManagementPropertyDocumentCategoryListPage} />
                      <Route exact path="/property_document/:id" component={ManagementPropertyDocumentListPage} />
                      <Route exact path="/add_new_property" component={ManagementAddNewOwnPropertyPage} />
                      <Route exact path="/add_new_property/consideration" component={ManagementAddNewConsiderationPropertyPage} />
                      <Route exact path="/add_new_property/building" component={ManagementAddNewOwnBuildingPropertyPage} />
                      <Route exact path="/edit_property" component={ManagementEditPropertyPage} />
                      <Route exact path="/edit_room" component={ManagementEditRoomPage} />
                      <Route exact path="/management/own" component={ManagementOwnPage} />
                      <Route exact path="/management/consideration" component={ManagementConsiderationPage} />
                      <Route exact path="/management/own/detail" component={ManagementDetailPage} />
                      <Route
                        exact
                        path="/management/consideration/detail"
                        component={ManagementConsiderationDetailPage}
                      />
                      <Route exact path="/management/detail/settings" component={ManagementDetailSettingsPage} />
                      <Route exact path="/limited_properties" component={LimitedPropertiesListPage} />
                      <Route exact path="/limited_properties/detail" component={LimitedPropertyDetailPage} />
                      <Route exact path="/agent_search" component={AgentSearchPage} />
                      <Route exact path="/agent_search/:id" component={AgentDetailPage} />
                      <Route
                        exact
                        path="/management/detail/simulation"
                        component={ManagementDetailSimulationConfigPage}
                      />
                      <Route exact path="/offer" component={OfferListPage} />
                      <Route exact path="/investment/:id" component={OfferInvestmentDetailPage} />
                      <Route exact path="/assessment/:id" component={OfferAssessmentDetailPage} />
                      <Route exact path="/balance" component={BalancePage} />
                      {/* 利用予定がないためコメントアウト */}
                      {/* <Route exact path="/journal" component={JournalPage} /> */}
                      <Route exact path="/learning/articles/:articleId" component={LearningArticlePage} />
                      <Route exact path="/learning/:stageId" component={LearningStagePage} />
                      <Route exact path="/learning" component={LearningTopPage} />
                      <Route exact path="/usage_guide" component={UsageGuidePage} />
                      <Route exact path="/settings" component={SettingsPage} />
                      <Route exact path="/webinars" component={PremiumWebinarListPage} />
                      <Route exact path="/webinars/:id" component={PremiumWebinarDetailPage} />
                      <Route component={NotFoundPage} />
                    </Switch>
                  </PreviousLocationProvider>
                </PropallyRecoilRoot>
              </WithAuth>
            </Switch>
          </Router>
        </RecoilRoot>
      </SnackbarProvider>
    </ThemeProvider>
  );
};
